import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/lib/language";

type Key = "detached" | "attached" | "garage";

const ITEMS: Record<Key, { path: string; en: string; es: string; dEn: string; dEs: string }> = {
  detached: {
    path: "/detached-adu-san-diego",
    en: "Detached ADU in San Diego",
    es: "ADU independiente en San Diego",
    dEn: "A separate backyard home with full privacy.",
    dEs: "Una casa separada en el patio con privacidad total.",
  },
  attached: {
    path: "/attached-adu-san-diego",
    en: "Attached ADU builders",
    es: "Constructores de ADU adosada",
    dEn: "Shares a wall with your home — often lower cost.",
    dEs: "Comparte pared con su casa — suele costar menos.",
  },
  garage: {
    path: "/garage-conversion-adu-san-diego",
    en: "Garage conversion ADU",
    es: "Conversión de garaje a ADU",
    dEn: "Turn your existing garage into a permitted unit.",
    dEs: "Convierta su garaje en una unidad con permisos.",
  },
};

const EXTRA = [
  { path: "/blog/ultimate-guide-adus-san-diego", en: "Complete San Diego ADU guide", es: "Guía completa de ADUs en San Diego" },
  { path: "/recursos/glosario", en: "ADU glossary", es: "Glosario de ADUs" },
];

/** Interlinking block pointing to the ADU service-type pages (+ optional resources). */
export default function ServiceTypeLinks({
  exclude,
  highlight,
  resources = false,
  title,
}: {
  exclude?: Key;
  highlight?: Key;
  resources?: boolean;
  title?: { en: string; es: string };
}) {
  const { lang } = useLang();
  const tr = (en: string, es: string) => (lang === "es" ? es : en);
  const keys = (Object.keys(ITEMS) as Key[])
    .filter((k) => k !== exclude)
    .sort((a, b) => (a === highlight ? -1 : b === highlight ? 1 : 0));

  return (
    <section className="py-12 md:py-16 bg-muted/40" aria-labelledby="service-types-heading">
      <div className="max-w-6xl mx-auto px-4">
        <h2 id="service-types-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground leading-snug">
          {title ? title[lang] : tr("ADU types we build", "Tipos de ADU que construimos")}
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {keys.map((k) => {
            const it = ITEMS[k];
            return (
              <Link
                key={k}
                to={it.path}
                className="group rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors"
              >
                <span className="font-heading font-bold text-foreground group-hover:text-primary inline-flex items-center gap-1">
                  {tr(it.en, it.es)} <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">{tr(it.dEn, it.dEs)}</span>
              </Link>
            );
          })}
        </div>
        {resources && (
          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {EXTRA.map((e) => (
              <li key={e.path}>
                <Link to={e.path} className="text-primary font-semibold hover:underline inline-flex min-h-[24px] items-center">
                  {tr(e.en, e.es)}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function serviceKeyForType(type: string): Key | undefined {
  const t = type.toLowerCase();
  if (t.includes("garage")) return "garage";
  if (t.includes("attached") && !t.includes("detached")) return "attached";
  if (t.includes("detached") || t.includes("new-adu")) return "detached";
  return undefined;
}
